import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useSearchParams } from 'react-router';
import { MUTATION_KEYS } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { useFetchEnterprise } from 'hooks/useFetchEnterprise';
import { useUserStore } from 'stores/user';
import { SignupForm } from './SignupForm';

export const Signup = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.AUTH);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const enterpriseId = searchParams.get('cid');
  const employee = useUserStore(state => state.user);
  const isProfileSetup = location.pathname === ROUTES.PROFILE_SETUP;
  const { isLoading: isLoadingEnterprise, data: enterprise } = useFetchEnterprise({
    enterpriseId,
    isProfileSetup,
  });
  const isMutating = useIsMutating({ mutationKey: [MUTATION_KEYS.UPDATE_EMPLOYEE] }) > 0;

  if (isProfileSetup && employee && !employee?.requiredProfileSetup) {
    return <Navigate to={ROUTES.HOME} />;
  }

  if (isLoadingEnterprise) {
    return <LoadingScreen />;
  }

  if (enterpriseId && !enterprise?.id && !isProfileSetup) {
    return (
      <div className="p-8">
        <Card className="p-4 bg-red-100 border-red-300">
          <p className="text-red-700 font-medium">{t('SignupForm.cid.error.invalid')}</p>
          <p className="text-denim-600 text-sm mt-6">{t('SignupForm.cid.error.body')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center bg-gray-50 p-4 min-h-screen overflow-y-auto max-am-xl:mt-4">
      <Card className="relative w-full bg-white rounded-2xl shadow-md am-md:max-w-[360px] am-xl:max-w-md am-xl:p-10">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-bold text-center">
            {isProfileSetup ? t('Register.profile') : t('Register.heading')}
          </h2>
          <p className="mt-2 text-gray-600 text-left max-[330px]:text-sm">
            {isProfileSetup ? '' : t('Register.body')}
          </p>
          {isMutating ? <LoadingScreen /> : <SignupForm isProfileSetup={isProfileSetup} />}
        </div>
      </Card>
    </div>
  );
};
